// Cliente del escaneo de marca del backend (`/v1/brand/escaneo`,
// ADR-2026-09-28-onboarding-v2-etapa-1): lanzarlo y leer su estado. El tablero lo polea
// y arma la pantalla con los hallazgos parciales.

import { atlasBackendFetch, atlasBackendGet } from '../atlasBackend';
import { SUPABASE_URL } from '../../config';
import { enGuion, escaneoDelGuion, ID_DEL_GUION, iniciarGuion } from './guion';

export interface ColorDeMarca {
  hex: string;
  name?: string;
  role?: 'primary' | 'secondary' | 'accent' | 'neutral' | string;
}

export interface TipografiasDeMarca {
  style?: string;
  suggested?: string[];
}

export interface HallazgosDelEscaneo {
  sitio?: {
    url: string;
    titulo: string;
    descripcion: string;
    nombre: string;
    imagen: string;
    color_tema: string;
  };
  /** URL de nuestro Storage: el logo del sitio ya copiado. */
  logo?: string;
  /** Captura de la home (CDN de microlink). */
  captura?: string;
  identidad?: {
    nombre: string;
    rubro: string;
    colores: ColorDeMarca[];
    tipografias: TipografiasDeMarca;
    analisis: string;
  };
}

export type EstadoDelEscaneo = 'en_cola' | 'analizando' | 'listo' | 'fallo';

export interface EscaneoDeMarca {
  estado: EstadoDelEscaneo;
  pasos: Array<{ nombre: string; estado: 'ok' | 'fallo' | 'salteado'; ms: number | null }>;
  hallazgos: HallazgosDelEscaneo;
  marca: { brand_id: string; nombre: string } | null;
  error: string | null;
}

export const ESCANEO_VACIO: EscaneoDeMarca = {
  estado: 'en_cola',
  pasos: [],
  hallazgos: {},
  marca: null,
  error: null,
};

export type InicioDelEscaneo =
  | { ok: true; escaneoId: string; link: string }
  | { ok: false; error: string };

/** `marcaNueva`: una marca ADICIONAL, sin tocar la default ni la empresa (ADR-2026-10-02-marca-nueva-como-el-onboarding). */
export interface OpcionesDelEscaneo {
  marcaNueva?: boolean;
}

export async function iniciarEscaneo(link: string, opciones: OpcionesDelEscaneo = {}): Promise<InicioDelEscaneo> {
  if (enGuion()) {
    iniciarGuion();
    return { ok: true, escaneoId: ID_DEL_GUION, link };
  }
  try {
    const res = await atlasBackendFetch('/v1/brand/escaneo', {
      website_url: link,
      ...(opciones.marcaNueva ? { marca_nueva: true } : {}),
    });
    const json = (await res.json().catch(() => ({}))) as {
      escaneo_id?: string;
      website_url?: string;
      error?: string;
    };
    if (res.ok && json.escaneo_id) {
      return { ok: true, escaneoId: json.escaneo_id, link: json.website_url ?? link };
    }
    if (res.status === 429) {
      return { ok: false, error: 'Hay muchos pedidos en curso. Espera unos segundos e intenta de nuevo.' };
    }
    return { ok: false, error: json.error || 'No pudimos empezar a analizar tu marca. Intenta de nuevo.' };
  } catch {
    return { ok: false, error: 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.' };
  }
}

export interface DatosManualesDeMarca {
  nombre: string;
  rubro: string;
  logoUrl: string | null;
  /** Qué vende, en una línea. */
  descripcion: string;
  /** A quién le vende (opcional). */
  publico: string;
}

/**
 * Sin link: el MISMO escaneo, por la rama manual del backend, con lo que contó la persona
 * (ADR-2026-09-29-onboarding-v2-camino-manual). Devuelve el id igual que con link.
 */
/**
 * ¿El logo está en NUESTRO Storage? El backend sólo describe logos de ahí (los baja con
 * sus credenciales); uno de un sitio ajeno —marcas viejas guardaban el favicon tal cual—
 * no se manda y la marca se arma igual, sin describirlo.
 */
export function logoDeAtlas(url: string | null): string | null {
  if (!url || !SUPABASE_URL) return null;
  try {
    const u = new URL(url);
    return u.origin === new URL(SUPABASE_URL).origin && u.pathname.startsWith('/storage/v1/object/') ? url : null;
  } catch {
    return null;
  }
}

export async function iniciarEscaneoManual(d: DatosManualesDeMarca, opciones: OpcionesDelEscaneo = {}): Promise<InicioDelEscaneo> {
  if (enGuion()) {
    iniciarGuion();
    return { ok: true, escaneoId: ID_DEL_GUION, link: '' };
  }
  try {
    const res = await atlasBackendFetch('/v1/brand/escaneo', {
      nombre_brand: d.nombre,
      rubro_brand: d.rubro,
      ...(logoDeAtlas(d.logoUrl) ? { logo_url: d.logoUrl } : {}),
      description_brand: d.descripcion,
      target_public_brand: d.publico,
      ...(opciones.marcaNueva ? { marca_nueva: true } : {}),
    });
    const json = (await res.json().catch(() => ({}))) as { escaneo_id?: string; error?: string };
    if (res.ok && json.escaneo_id) return { ok: true, escaneoId: json.escaneo_id, link: '' };
    if (res.status === 429) {
      return { ok: false, error: 'Hay muchos pedidos en curso. Espera unos segundos e intenta de nuevo.' };
    }
    return { ok: false, error: json.error || 'No pudimos armar tu marca. Intenta de nuevo.' };
  } catch {
    return { ok: false, error: 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.' };
  }
}

/** Estado del escaneo, o null si no se pudo leer (un blip de red: el poll sigue). */
export async function leerEscaneo(escaneoId: string): Promise<EscaneoDeMarca | null> {
  // El id del guion nunca va al backend: con el guion apagado es un escaneo que no existe.
  if (escaneoId === ID_DEL_GUION) {
    return enGuion() ? escaneoDelGuion() : { ...ESCANEO_VACIO, estado: 'fallo', error: 'Ese análisis era de prueba. Vuelve a empezar.' };
  }
  try {
    const res = await atlasBackendGet(`/v1/brand/escaneo/${encodeURIComponent(escaneoId)}`);
    if (res.status === 404) {
      return { ...ESCANEO_VACIO, estado: 'fallo', error: 'No encontramos ese análisis. Vuelve a empezar.' };
    }
    if (!res.ok) return null;
    const json = (await res.json()) as Partial<EscaneoDeMarca>;
    return {
      estado: json.estado ?? 'analizando',
      pasos: Array.isArray(json.pasos) ? json.pasos : [],
      hallazgos: (json.hallazgos ?? {}) as HallazgosDelEscaneo,
      marca: json.marca ?? null,
      error: json.error ?? null,
    };
  } catch {
    return null;
  }
}
